/**
 *  -------------------------------
 *  |    topBar                   |
 *  _______________________________
 *  |                             |
 *  |      previewAndEditor       |
 *  |                             |
 *  |                             |
 *  |                             |
 *  -------------------------------
 *  |     previewBar              |
 * -------------------------------
 */


export const measures = {
  topBar: {
    landscape: {
      /* floor keeps 48px gold-standard targets inside the bar with air to spare */
      height: "max(6.2svh, 60px)",
      icon: "3.4svh",
      iconMargin: "0.7svw",
      // wide enough that the shortest label ("New") reaches the 48px target
      controlPaddingInline: "0.85em"
    },
    portrait: {
      height: "max(8svh, 60px)",
      icon: "4.4svh",
      iconMargin: "2.5svw"
    },

    inlineMargin: {
      portrait: "unset",
      landscape: "1svw"
    }

  },
  previewAndEditor: {
    landscape: {
      height: "80.5svh",
      /* editor absorbs whatever the floored bars claim; rows still sum to 100svh */
      editorRowHeight: "calc(100svh - max(6.2svh, 60px) - max(7svh, 58px) - 13.3svh)",
    }
  },
  gridColumns: {
    landscape: {
      left: "50svw",
      right: "50svw",
    }
  },
  previewBar: {
    height: "13.3svh",
  },
  previewButtons: {
    barHeight: "5svh",
    rowHeight: "9svh",
    icon: "3svh",
    padding: "1svh 1.5svw"
  },
  dialogPanel: {
    landscape: {
      margin: "1.6svw",
      /* floor keeps 48px pills + headers breathing on short windows */
      height: "min(max(60svh, 520px), 92svh)"
    },
    portrait: {
      margin: "4svw",
      height: "90svh"
    },
    rowHeight: "6svh"
  },
  editor: {
    landscape: {
      height: "100%",
      width: "100%",
      gutter: "2.7svw",
    },
    portrait: {
      height: "100%",
      width: "100svw",
    },
  },
  infoBar: {
    landscape: {
      height: "4svh",
    },
    portrait: {
      height: "4svh",
    },
  },
  input: {
    width: "50svw",
  },
  navigationControlBar: {
    landscape: {
      height: "4.6svh",
      icon: "2svh",
      button: "2svh"
    },
    portrait: {
      height: "9.7svh",
      icon: "6svh",
      button: "6svh"
    },

  },
  output: {
    width: "40svw",
  },
  thumbnails: {
    width: "10svw",
  },

  previewPane: {
    landscape: {
      soloHeight: "38svh",
      sharedHeight: "55svh",
    },
    portrait: {
      soloHeight: "60svh",
      sharedHeight: "55svh",
    },
  },

  previewImage: {
    height: "67%"
  },
  sideBar: {
    width: "11svw",
    height: "100%"
  },
  styledApp: {
    height: "100svh",
  },
  textControlBar: {
    landscape: {
      height: "max(4.6svh, 48px)", /* floor keeps tab targets >=44px on short windows */
      formatIcon: "2.5svh",
      controlButton: "1.6svh",
      iconMargin: "0.5svw"
    },
    portrait: {
      height: "7svh",
      formatIcon: "4svh",
      controlButton: "4svh",
      iconMargin: "2svw"
    },

    controlIcon: "1.57svh",

  },
  vocabBar: {
    landscape: {
      height: "5.5svh",
      /* floor: 48px tokens + area padding; anything more is the editor's */
      rowHeight: "max(7svh, 58px)",
      tokenHeight: "3.5svh",
      tokenPadding: "0.8svh 1svw",
      gap: "0.5svw",
    },
    portrait: {
      height: "6.5svh",
      tokenHeight: "4svh",
      tokenPadding: "1svh 1.8svw",
      gap: "0.9svw",
    },
  },
} as const;

/** Reusable :focus-visible CSS for all interactive elements (WCAG 2.4.7) */
export const focusVisible = `
  &:focus-visible {
    outline: 3px solid #4A90D9;
    outline-offset: 2px;
  }
  &:focus:not(:focus-visible) {
    outline: none;
  }
`;

export const colors = {
  aliceblue: "aliceblue",
  black: "#000",
  editorBorderGrey: "#c8c8c8",
  grey: "#95999D",
  lightGrey: "#f0f0f0",
  offWhite: "#f7f7f7",
  positionMarkerRed: "#FF726A",
  positionMarkerBlue: "#7fd1f6",
  red: "#FF6961",
  teal: "#14505F", /* link color: 8.9:1 on white, 7.8:1 on the lavender hover tints (AAA); was #1D5E75 at 6.3:1 on #F0EDFC */
  transWhite: "rgba(255, 255, 255, 0.9);",
  white: "#FFF",
  yellow: "#EBB822",
  /* AAA text-safe variants (>=7:1 on white and the app's tinted surfaces).
     Use these when the colour IS small text; the alabaster/brand values stay
     for fills, icons (>=3:1) and large text. */
  textSafe: {
    blue: "#005A96",
    red: "#992F29",
    green: "#2E6118",
    magenta: "#6E3390",
    grey: "#4F4F4F"
  },
  alabaster: {
    "active": "#007ACC",
    "background": "#F7F7F7",
    "selection": "#BFDBFE",
    "blue": "#325CC0",
    "green": "#448C27",
    "red": "#AA3731",
    "magenta": "#7A3E9D",
    "grey": "#757575",
    "orange": "#FFBC5D"
  },
  brand: {
    white: "#ffffff",
    black: "#000000",
    purple: "#6B4FDB",
    blue: "#BCE4E7",
    yellow: "#F4EC7A",
    gray: "#E6E6E6"
  },
  tufte: {
    white: "#fffff8",
    black: "#111111",
    red: "#dc2828"
  }
} as const;








